import { useAuth, useClerk, useOrganization } from "@clerk/react";
import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react";

import { hasClerkPublishableKey } from "../../cloud/publicConfig";
import { markClerkReady, publishClerkTokenGetter } from "../../clerkTokenBridge";
import { resolveComposioUserId } from "../../composioConnect";
import { isElectron } from "../../env";
import { resolveClerkSignInProps } from "./authRedirect";
import { resolveMorrowAuthWelcomeSignInProps } from "./MorrowAuthWelcome.logic";
import { toastManager } from "../ui/toast";

// How long a click waits for Clerk's script before we tell the user sign-in is blocked.
const CLERK_LOAD_GRACE_MS = 5_000;

/**
 * Open a Clerk modal, but never fail silently. Before Clerk's script has loaded,
 * `openSignIn` just queues — and if the script is blocked (ad blocker, privacy
 * extension, a network that blocks the Clerk domain) the click does nothing at all.
 * So when Clerk isn't ready, give it a short grace period and then say why.
 */
function openWhenClerkLoads(
  clerk: { readonly loaded: boolean; readonly status?: string },
  open: () => void,
): void {
  open();
  if (clerk.loaded) return;
  const warn = () =>
    toastManager.add({
      type: "error",
      title: "Sign-in couldn't load",
      description: "Try turning off your ad blocker or using another network, then reload.",
    });
  if (clerk.status === "error") {
    warn();
    return;
  }
  setTimeout(() => {
    if (!clerk.loaded) warn();
  }, CLERK_LOAD_GRACE_MS);
}

/**
 * Clerk's real authorization checker, derived straight from `useAuth().has` so it
 * stays in lockstep with the installed Clerk version. It accepts role, permission,
 * plan, feature, or scope checks — e.g. `has({ role: "org:admin" })` or
 * `has({ permission: "org:sys_memberships:manage" })`. (Client checks are only
 * cosmetic; the server `requireOrgPermission` gate is the real boundary.)
 */
export type ClerkHas = ReturnType<typeof useAuth>["has"];

export interface MorrowClerkAuth {
  readonly isLoaded: boolean;
  readonly isSignedIn: boolean;
  readonly userId: string | null;
  readonly openSignIn: () => void;
  readonly openSignUp: () => void;
  // Undefined when signed out (no Clerk checker yet); callers use `has?.(…)`.
  readonly has: ClerkHas | undefined;
  // True only when a Clerk Organization is the session's active org. A personal
  // account (no active org) reports false, so org-only surfaces (e.g. the Team
  // nav item) can hide. Always false on non-Clerk builds — use
  // `useShowOrgWorkspaceNav()` rather than reading this directly for nav gating.
  readonly hasActiveOrganization: boolean;
  // The active Clerk organization id (`org_…`), or null for personal / non-Clerk.
  readonly orgId: string | null;
}

const SIGNED_OUT: MorrowClerkAuth = {
  isLoaded: true,
  isSignedIn: false,
  userId: null,
  openSignIn: () => {},
  openSignUp: () => {},
  has: undefined,
  hasActiveOrganization: false,
  orgId: null,
};

const MorrowClerkAuthContext = createContext<MorrowClerkAuth>(SIGNED_OUT);

function ConfiguredMorrowClerkAuthProvider({ children }: { readonly children: ReactNode }) {
  // `treatPendingAsSignedOut: false` — a user who has authenticated but whose
  // Clerk session is "pending" (e.g. Organizations are in "Membership required"
  // mode and no active org is resolved for this session yet) is still a real,
  // signed-in user to us. The default (`true`) reports them as signed OUT, which
  // made the landing show a "log in" prompt and the composer fall back to a guest
  // id even though they're logged in. This matches cloud/managedAuth.tsx.
  const { isLoaded, isSignedIn, userId, has } = useAuth({ treatPendingAsSignedOut: false });
  const clerk = useClerk();
  // The session's active organization, if any. `useOrganization` is safe here
  // (we are inside <ClerkProvider />) and returns `{ organization: null }` both
  // for personal accounts and when Organizations are disabled on the instance —
  // it does not throw the way the <OrganizationSwitcher> primitive does.
  const { organization } = useOrganization();
  const hasActiveOrganization = Boolean(organization);

  // Publish the token getter for the Effect HTTP layer (see clerkTokenBridge).
  // The hosted web client sends this JWT as `x-clerk-token` so the backend can
  // mint a per-user session without a shared cookie across subdomains.
  useEffect(() => {
    publishClerkTokenGetter(() =>
      clerk.session ? clerk.session.getToken() : Promise.resolve(null),
    );
  }, [clerk]);

  // Once Clerk has loaded (signed in or not), unblock the auth bootstrap so the
  // first /auth/session request carries the token when a session exists. Persist
  // the Clerk owner cookie immediately so the next WebSocket / snapshot is not
  // still scoped to the boot guest id.
  useEffect(() => {
    if (!isLoaded) return;
    markClerkReady();
    if (isSignedIn && userId) {
      resolveComposioUserId(userId);
    }
  }, [isLoaded, isSignedIn, userId]);

  const value = useMemo<MorrowClerkAuth>(
    () => ({
      isLoaded,
      isSignedIn: Boolean(isSignedIn),
      userId: userId ?? null,
      openSignIn: () =>
        openWhenClerkLoads(clerk, () =>
          clerk.openSignIn(
            resolveMorrowAuthWelcomeSignInProps(
              typeof window === "undefined" ? "/" : window.location.href,
              isElectron,
            ),
          ),
        ),
      openSignUp: () =>
        openWhenClerkLoads(clerk, () =>
          clerk.openSignUp(
            resolveClerkSignInProps(
              typeof window === "undefined" ? "/" : window.location.href,
              isElectron,
            ),
          ),
        ),
      has,
      hasActiveOrganization,
      orgId: organization?.id ?? null,
    }),
    [clerk, has, hasActiveOrganization, isLoaded, isSignedIn, userId, organization?.id],
  );
  return (
    <MorrowClerkAuthContext.Provider value={value}>{children}</MorrowClerkAuthContext.Provider>
  );
}

/**
 * Safe Clerk session for MorrowOS. Clerk hooks only run when a publishable key
 * mounted <ClerkProvider />; otherwise the tree gets a signed-out default so
 * sidebar/chat/settings can render without crashing.
 */
export function MorrowClerkAuthProvider({ children }: { readonly children: ReactNode }) {
  if (!hasClerkPublishableKey()) {
    return (
      <MorrowClerkAuthContext.Provider value={SIGNED_OUT}>
        {children}
      </MorrowClerkAuthContext.Provider>
    );
  }
  return <ConfiguredMorrowClerkAuthProvider>{children}</ConfiguredMorrowClerkAuthProvider>;
}

export function useMorrowClerkAuth(): MorrowClerkAuth {
  return useContext(MorrowClerkAuthContext);
}

/**
 * Whether org-only workspace surfaces (currently the "Team" nav item and its
 * command-palette action) should be shown.
 *
 * - Hosted / Clerk builds: shown only when the session has an active
 *   organization. A personal (individual) account has no team, so it hides.
 * - Non-Clerk desktop / self-hosted builds: Clerk Organizations don't exist, so
 *   there is nothing to gate on — keep the current always-visible behavior.
 */
export function useShowOrgWorkspaceNav(): boolean {
  const { hasActiveOrganization } = useMorrowClerkAuth();
  return !hasClerkPublishableKey() || hasActiveOrganization;
}
